import { useEffect, useLayoutEffect, useState } from 'react';

import { ConfigNumberParameters } from '@/data/libraries/ConfigParameters';

import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import type { Location, NavigateFunction } from 'react-router-dom';

import { getRoutesDirections } from '@/data/libraries/Routes';
import { isEmpty } from 'lodash';

import { useScreenSize, type ScreenSize } from '@/ui/hooks/useScreenSize';
import useReservation from '@/ui/hooks/useReservation';
import useAttributesPresenter from '@/ui/hooks/useAttributesPresenter';

import { Col, Container, Row, Image, Button, Modal } from 'react-bootstrap';

import { useAppDispatch, useAppSelector } from '@/ui/store/helperRedux';
import { RootState } from '@/ui/store/store';
import { updateIndexCheckOut } from '@/ui/store/slices/indexCheckOutSlice';
import { changeLoginState } from '@/ui/store/slices/showModalsSlice';

import CartController from '@/controllers/CartController';

import { IBreadcrumbs } from '@/domain/interfaces/IBreadcrumbs';
import { ICart, ICartResponse } from '@/domain/interfaces/ICart';
import { IStore } from '@/domain/interfaces/IStore';
import { translate } from '@/domain/utils/translate';
import currencyFormat from '@/domain/utils/currencyFormat';
import lastPageName from '@/domain/utils/lastPageName';

import ErrorImage from '@/ui/assets/ErrorImage';

import Breadcrumbs from '@/ui/components/Breadcrumbs';
import IconList from '@/ui/components/IconList';
import EmptyContent from '@/ui/components/EmptyContent';
import ProductCounter from '@/ui/components/product/ProductCounter';
import ReservationsModals from '@/ui/components/product/ReservationsModals';
import ProductStatusPill from '@/ui/components/product/ProductStatusPill';
import MainPrice from '@/ui/components/product/MainPrice';
import SecondaryPrice from '@/ui/components/product/SecondaryPrice';
import ShippingMethodsIcons from '@/ui/components/product/ShippingMethodsIcons';
import CartAlertModal from '@/ui/components/modals/CartAlertModal';


/**
 * Componente Carrito de Compras
 * @component
 */
const Cart = (): JSX.Element => {
  /** VARIABLES DE ENTORNO */
  const URL_IMAGES: string = process.env.REACT_APP_URL_IMAGES ?? '';
  /** Hook que contiene las dimensiones de la pantalla y maneja los componentes de acuerdo al resultado obtenido */
  const { width, maxLargeWidth }: ScreenSize = useScreenSize();
  /** Variable que define si es entorno mobile o desktop */
  const isMobile: boolean = width < maxLargeWidth;
  /** Controlador de carrito */
  const { productQuantity, deleteCart, updateCartProductsData, validateCart } = CartController();

  /** Hook para trabajar con document location  */
  const location: Location = useLocation();

  /** Hook actualización Redux */
  const dispatch = useAppDispatch();


  /** Función que muestra el modal de login */
  const handleShowLogin = () => {
    dispatch(changeLoginState())
  }

  /**Alerta que bloquean el carrito*/
  const [cartError, setCartError] = useState<boolean>(false);
  const [cartErrorMessage, setCartErrorMessage] = useState<string>('');

  // Estado para controlar si el carrito está siendo procesado
  const [isProcessingCart, setIsProcessingCart] = useState<boolean>(false);
  // Estado para controlar si el carrito está cargando
  const [isLoading, setIsLoading] = useState<boolean>(true);

  /**Variable que define si se debe reservar o no */
  const [shouldReserve, setShouldReserve] = useState(false);

  /**Alerta que genera warning */
  const [cartWarning, setCartWarning] = useState<boolean>(location.state.cartError ?? false);
  const [cartWarningMessage, setCartWarningMessage] = useState<string>(location.state.errorMessage ?? '');

  /** Hook para navegar entre páginas   */
  const navigate: NavigateFunction = useNavigate();

  /** Trae la data del usuario registrado */
  const userLogued = useAppSelector((state) => state.userState);
  const userStore: string = String(userLogued.user?.userStore ?? 1);

  /** Rutas URLs */
  const routesDirections = getRoutesDirections();

  /** Listado de breadcrumbs de la página */
  const breadcrumbsList: IBreadcrumbs[] = [
    { label: translate('breadcrumbs.home'), url: routesDirections.MAIN_ROUTE },
    { label: lastPageName(location.pathname), url: location.pathname },
  ];

  /**Trae la información del carrito desde Redux */
  const cartProducts = useAppSelector((state: RootState) => state.cartState) as ICart;

  //Transforma atributos en un objeto como se presentan para el cliente
  const { attributesPresenter } = useAttributesPresenter();
  const [showReservationModal, setShowReservationModal] = useState<boolean>(false);
  const [showErrorReservationModal, setShowErrorReservationModal] = useState<boolean>(false);

  /**Modal para inactividad del usuario */
  const [showModalIdle, setShowModalIdle] = useState<boolean>(location.state?.showIdleModal);

  /**Caso de uso que modifica el estado de la  location luego de renderizar */
  useEffect(() => {
    /** Caso de uso que modifica el estado de la  location luego de renderizar */
    if (showModalIdle) {
      navigate(location, { state: { originURL: location.pathname, showModalIdle: false } });
    }
  }, []);

  const reservationHook = useReservation();

  /**Lógica para generar errores si hay cambios en el carrito o realizar la reserva si no */
  const handleReservation = async () => {



    setCartWarning(false);
    setCartWarningMessage('');
    setShowReservationModal(true);

    const response = await cartRefresh();

    if (response.error) {
      setTimeout(() => {
        setShowReservationModal(false);
        setCartWarning(response.error);
        setCartWarningMessage(response.message);
      }, 1000);
      return;
    }

    setTimeout(async () => {
      const isReserved = await reservationHook.reservation();
      if (!isReserved) {
        setShowReservationModal(false);
        setShowErrorReservationModal(true);
      } else {
        navigate(routesDirections.CHECK_OUT_ROUTE, { state: { originURL: location.pathname } });
      }
    }, 2000);
  };
  /** Estado Redux Tienda */
  const storeData = useAppSelector((state: RootState) => state.storeData) as IStore | null;


  /**Función que verifica si el usuario está logueado o no */
  const reservationWithLogin = () => {
    if (!userLogued.user.isLogued) {
      handleShowLogin();
      setShouldReserve(true);
    } else {
      handleReservation();
    }
  };

  /**Efecto que verifica si el usuario está logueado y si debe reservar */
  useEffect(() => {
    if (userLogued.user.isLogued && shouldReserve) {
      handleReservation();
      setShouldReserve(false);
    }
  }, [userLogued.user.isLogued, shouldReserve]);

  /**funcion que se ejecuta si es solo compra obligatoria */
  const purchaseOnlyClient = () => {
    if (storeData?.purchaseOnlyClient) {
      reservationWithLogin()

    } else {
      handleReservation()
    }
  }


  /** Actualiza datos de los productos del carrito */
  const cartRefresh = async (): Promise<ICartResponse> => {
    try {
      const result = await updateCartProductsData();
      if (result.error) {
        setCartWarning(result.error);
        setCartWarningMessage(result.message);
      }
      return result;
    } catch (error) {
      // Verificamos si el error es una instancia de Error
      const errorMessage = error instanceof Error ? error.message : ERROR_CART;
      setCartError(!errorMessage);
      setCartErrorMessage(errorMessage);
      return { error: true, cart: null, message: errorMessage };
    }
  };

  /** Al cambiar el carrito se aplica el hook que valida y alerta */
  useLayoutEffect(() => {
    const responseValidateCart: ICartResponse = validateCart();
    setCartError(responseValidateCart.error);
    setCartErrorMessage(responseValidateCart.message);
  }, [cartProducts]);

  /** Efecto para actualizar el carrito y el índice de checkout */
  useLayoutEffect(() => {
    if (!cartError) {
      cartRefresh();
      dispatch(updateIndexCheckOut(0));
    }
  }, []);

  /**CONSTANTES DE TEXTO */

  const CART_TITLE: string = translate('cart.cart_title');

  const CART_RESUMEN: string = translate('checkout.cart_resumen');
  const CART_SUBTOTAL: string = translate('cart.cart_subtotal');
  const CART_SUBTOTAL_MESSAGE: string = translate('checkout.subtotal_message');
  const BUY_BUTTON_TEXT: string = translate('checkout.buy_button_text');
  const CLEAN_CART_TEXT: string = translate('cart.clean_cart_text');
  const CART_ITEMS_COUNTER_LABEL: string = productQuantity() > 1 ? translate('product.products') : translate('product.product');

  const MODAL_IDLE_URL: string = `${URL_IMAGES}/client/idle-modal.png`;
  const MODAL_IDLE_TITLE: string = translate('checkout.idle_title');
  const MODAL_IDLE_UPDATE_BUTTON: string = translate('checkout.idle_update_button');
  const ERROR_CART: string = translate('cart.error_cart');

  /**COMPONENTES BASE */

  /**Componente Imagen */
  const PreviewImage = ({ product }): JSX.Element => {
    return (
      <div className='image-cart-container '>
        <NavLink to={`${routesDirections.PRODUCT_ROUTE}/${userStore}/${product.skuProduct}`} className='image-cart-container rounded rounded-3'>
          <Image
            className='rounded rounded-3 image-full cursor-hand'
            src={`${product.image}`}
            alt={''}
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              e.currentTarget.src = ErrorImage;
            }}
          />
        </NavLink>
      </div>
    );
  };

  // Efecto para restablecer isProcessingCart cuando cambia el estado del carrito
  useEffect(() => {
    setIsProcessingCart(false);
  }, [cartProducts]);

  /** Carga los datos del carrito y actualiza el estado de carga */
  const loadCartData = async (): Promise<void> => {
    setIsLoading(true); // Inicia la carga

    try {
      // Asegurar que se carguen los productos
      if (cartProducts.details && cartProducts.details.length > 0) {
        await cartRefresh();
      }
    } catch (error) {
      // Error eliminado
    } finally {
      // Finaliza la carga después de un tiempo para asegurar que la UI se haya actualizado
      setTimeout(() => {
        setIsLoading(false);
      }, 1000); // Usar 1 segundo para dar tiempo a la carga
    }
  };

  // Efecto para cargar la información del carrito
  useEffect(() => {
    loadCartData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Container fluid className='p-0 min-vh-50 bg-primary-5'>
        {!isEmpty(cartProducts) && cartProducts.details && cartProducts.details.length > 0 ? (
          <Container fluid>
            {isMobile ? (
              <>
                <Row className=' pt-4  g-0'>
                  <Col xxl={12} className='p-2 bg-secondary-5 shadow rounded'>
                    <Breadcrumbs />
                  </Col>
                </Row>
                <Row className='pt-3 pb-3  g-0'>
                  <Col xs={12} className='py-2 '>
                    <Row
                      xs={12}
                      className='d-flex g-0 py-2 pe-2 justify-content-between align-items-center bg-secondary-5 shadow rounded row-cols-auto'>
                      <Col>
                        <IconList
                          classMain='ps-3 text-secondary-1'
                          classIcon='svg-secondary-1 pe-2'
                          title={CART_TITLE}
                          iconName='ico-market-car'
                          classTitle='p-large-bold'
                        />
                      </Col>
                      <Col>
                        <span className='my-2 p-regular-bold ps-3 text-secondary-1'>{`${productQuantity()} ${CART_ITEMS_COUNTER_LABEL}`}</span>
                      </Col>
                    </Row>
                    {cartWarning && <CartAlertModal message={cartWarningMessage} type={'warning'} onClose={() => setCartWarning(false)} />}
                    {cartError && <CartAlertModal message={cartErrorMessage} type={'warning'} />}

                    {!isEmpty(cartProducts) &&
                      cartProducts.details.map((product) => {
                        const isInOffer: boolean = product.offerPrice !== undefined && product.offerPrice !== null;
                        const isFree: boolean = product.offerPrice !== undefined && product.offerPrice !== null && product.offerPrice === 0;

                        const attributes: string = attributesPresenter(product.attributes);
                        return (
                          <Container fluid className='p-0 bg-secondary-5 my-3 rounded rounded-3' key={product.skuProduct}>
                            <Row className='g-0 p-1 d-flex '>
                              <Col xs={5} className='mx-auto text-center position-relative'>
                                <PreviewImage product={product} />
                                <div className='top-0 end-0 position-absolute pe-2'>
                                  <ProductStatusPill isInOffer={isInOffer} isSoldOut={product.quantityAvailable === 0} />{' '}
                                </div>
                              </Col>
                              <Col xs={7} className='ps-3 pe-2 position-relative '>
                                <Row className='g-0 d-flex '>
                                  <Col xs={12} className='p-regular text-truncate'>
                                    <span className='p-regular-medium'>{product.title}</span>
                                  </Col>
                                  <Col xs={12} className='text-truncate'>
                                    <span className='p-small'>{attributes}</span>
                                  </Col>
                                  <Row className='my-2 g-0'>
                                    <Col xs={12} className='p-large-bold'>
                                      <MainPrice
                                        isFree={isFree}
                                        value={isInOffer ? product.offerPrice ?? 0 : product.price}
                                        className='p-title-bold'
                                      />
                                    </Col>
                                    <Col xs={12} className='p-large-medium'>
                                      <SecondaryPrice isInOffer={isInOffer} value={product.price} className='pe-3 p-regular-medium' />
                                    </Col>
                                  </Row>
                                </Row>
                                <Row className='g-0 mb-1 row-cols-auto'>
                                  <ShippingMethodsIcons data={product.shippingTypes} classTitle='fw-bold pe-2' />
                                </Row>
                              </Col>
                            </Row>
                            <Row className='g-0 px-1'>
                              <Col xs={12}>
                                <ProductCounter skuProduct={product.skuProduct} productSelected={true} />
                              </Col>
                            </Row>
                          </Container>
                        );
                      })}
                  </Col>
                  <Col xs={12}>
                    <Container fluid className='p-0'>
                      <Row className='g-0 py-2 px-4  d-flex justify-content-start bg-secondary-5 shadow rounded '>
                        <Col xs={12}>
                          <h4 className='text-secondary-1 mb-4 mt-2 text-center'>{CART_RESUMEN}</h4>
                        </Col>
                        <Col xs={6}>
                          <h4 className='text-secondary-2'>{CART_SUBTOTAL}</h4>
                          <span className='p-regular text-secondary-2'>{CART_SUBTOTAL_MESSAGE}</span>
                        </Col>
                        <Col xs={6} className='text-end'>
                          <span className='p-large-bold text-secondary-2'>
                            {currencyFormat(cartProducts.totalWithTax, ConfigNumberParameters.DECIMALS_ALLOWED)}
                          </span>
                        </Col>
                        <Col xs={12}>
                          <Button
                            className={`my-2 w-100 svg-primary-4 btn-primary-icon-standard ${cartError ? 'disabled' : ''}`}
                            type='button'
                            onClick={purchaseOnlyClient}>
                            <span className={`pe-2 p-regular-medium`}>{BUY_BUTTON_TEXT}</span>
                          </Button>
                        </Col>
                        <Col xs={12} className='text-center my-2'>
                          <button
                            className={`h5 link-text-standard ${isLoading || isProcessingCart ? 'disabled opacity-50' : ''}`}
                            disabled={isLoading || isProcessingCart}
                            onClick={async (e) => {
                              e.preventDefault();
                              if (isLoading || isProcessingCart) return; // Evitar clics si está cargando o procesando

                              // Marcar como procesando
                              setIsProcessingCart(true);

                              try {
                                // Llamar a deleteCart que ahora devuelve una promesa
                                const success = await deleteCart();

                                if (success) {
                                  // Limpiar estados después de eliminar el carrito
                                  setCartError(false);
                                  setCartWarning(false);
                                } else {
                                  // Mensaje de error eliminado
                                }
                              } catch (error) {
                                // Error log eliminado
                              } finally {
                                // Asegurar que se actualice el estado de procesamiento
                                setIsProcessingCart(false);
                              }
                            }}
                          >
                            {CLEAN_CART_TEXT}
                          </button>
                        </Col>
                      </Row>
                    </Container>
                  </Col>
                </Row>
              </>
            ) : (
              <Container className='p-0'>
                <Row className=' pt-4  g-0'>
                  <Col xxl={12} className='p-2 bg-secondary-5 shadow rounded'>
                    <Breadcrumbs list={breadcrumbsList} />
                  </Col>
                </Row>
                <Row className='pt-3 pb-3  g-0'>
                  <Col lg={8} className='py-2 '>
                    <Row xs={12} className='d-flex g-0 py-2 justify-content-between align-items-center bg-secondary-5 shadow rounded row-cols-auto'>
                      <Col>
                        <IconList
                          classMain='ps-3 text-secondary-1'
                          classIcon='svg-secondary-1 pe-2'
                          title={CART_TITLE}
                          iconName='ico-market-car'
                          classTitle='h2'
                        />
                      </Col>
                      <Col>
                        <span className='my-2 h4 pe-3 text-secondary-1'>{`${productQuantity()} ${CART_ITEMS_COUNTER_LABEL}`}</span>
                      </Col>
                    </Row>
                    {cartWarning && <CartAlertModal message={cartWarningMessage} type={'warning'} onClose={() => setCartWarning(false)} />}
                    {cartError && <CartAlertModal message={cartErrorMessage} type={'warning'} />}

                    {!isEmpty(cartProducts) &&
                      cartProducts.details.map((product) => {
                        const isInOffer: boolean = product.offerPrice !== undefined && product.offerPrice !== null;
                        const isFree: boolean = product.offerPrice !== undefined && product.offerPrice !== null && product.offerPrice === 0;
                        const attributes: string = attributesPresenter(product.attributes);
                        return (
                          <Container fluid className='p-2 bg-secondary-5 my-2 shadow rounded' key={product.skuProduct}>
                            <Row className={'g-0'}>
                              <Col lg={2}>
                                <PreviewImage product={product} />
                              </Col>
                              <Col lg={10} className='ps-3'>
                                <Row className='g-0'>
                                  <Col xs={9} className='d-flex justify-content-between align-items-center text-truncate'>
                                    <span className='paragraph text-truncate pe-2'>{product.title}</span>
                                  </Col>
                                  <Col lg={3} className='d-flex align-items-center justify-content-between '>
                                    <ShippingMethodsIcons data={product.shippingTypes} classTitle='fw-bold' />
                                  </Col>
                                  <Col xs={12}>
                                    <span>{attributes}</span>
                                  </Col>
                                </Row>
                                <Row className={'g-0 mt-4 d-flex align-items-center'}>
                                  <Col lg={5}>
                                    <Col xs={12} className='d-flex align-items-center p-large-bold'>
                                      <MainPrice isFree={isFree} value={isInOffer ? product.offerPrice ?? 0 : product.price} className='pe-3' />
                                    </Col>
                                    <Col xs={12} className='d-flex align-items-center p-regular-medium'>
                                      <SecondaryPrice isInOffer={isInOffer} value={product.price} />
                                    </Col>
                                  </Col>
                                  <Col lg={2} xxl={1} className='text-start'>
                                    <ProductStatusPill isInOffer={isInOffer} isSoldOut={product.quantityAvailable === 0} />
                                  </Col>
                                  <Col lg={5} className='text-end ms-auto'>
                                    <ProductCounter skuProduct={product.skuProduct} productSelected={true} />
                                  </Col>
                                </Row>
                              </Col>
                            </Row>
                          </Container>
                        );
                      })}
                  </Col>
                  <Col lg={4} className='ms-auto pt-2 ps-3'>
                    <Container fluid className='p-0'>
                      <Row className='g-0 py-2 px-4  d-flex justify-content-start align-items-center bg-secondary-5 shadow rounded '>
                        <Col xs={12}>
                          <h4 className='text-secondary-1 mb-4 mt-2'>{CART_RESUMEN}</h4>
                        </Col>
                        <Col xs={5}>
                          <h4 className='text-secondary-2'>{CART_SUBTOTAL}</h4>
                        </Col>
                        <Col xs={7} className='text-end'>
                          <span className='h4 text-secondary-2'>
                            {currencyFormat(cartProducts.totalWithTax, ConfigNumberParameters.DECIMALS_ALLOWED)}
                          </span>
                        </Col>
                        <Col xs={12}>
                          <span className='p-large text-secondary-2'>{CART_SUBTOTAL_MESSAGE}</span>
                        </Col>
                        <Col xs={12} className=' my-3'>
                          <Button
                            className={`my-2 w-100 svg-primary-4 btn-primary-icon-standard ${cartError ? 'disabled' : ''}`}
                            type='button'
                            onClick={purchaseOnlyClient}>
                            <span className={`pe-2 p-regular-medium`}>{BUY_BUTTON_TEXT}</span>
                          </Button>
                        </Col>
                        <Col xs={12} className='text-center my-3'>
                          <button
                            className={`h5 link-text-standard ${isLoading || isProcessingCart ? 'disabled opacity-50' : ''}`}
                            disabled={isLoading || isProcessingCart}
                            onClick={async (e) => {
                              e.preventDefault();
                              if (isLoading || isProcessingCart) return; // Evitar clics si está cargando o procesando

                              // Marcar como procesando
                              setIsProcessingCart(true);

                              try {
                                // Llamar a deleteCart que ahora devuelve una promesa
                                const success = await deleteCart();

                                if (success) {
                                  // Limpiar estados después de eliminar el carrito
                                  setCartError(false);
                                  setCartWarning(false);
                                } else {
                                  // Mensaje de error eliminado
                                }
                              } catch (error) {
                                // Error log eliminado
                              } finally {
                                // Asegurar que se actualice el estado de procesamiento
                                setIsProcessingCart(false);
                              }
                            }}
                          >
                            {CLEAN_CART_TEXT}
                          </button>
                        </Col>
                      </Row>
                    </Container>
                  </Col>
                </Row>
              </Container>
            )}
          </Container>
        ) : (
          <>
            {isMobile ? <EmptyContent /> : <Container fluid className=' my-5 px-5'>
              <Row className='min-vh-75 g-0 px-5 mx-5'>
                <EmptyContent />

              </Row>
            </Container>}

          </>
        )}
      </Container>

      {/* Inicio Modal inactividad */}
      {showModalIdle && (
        <Modal
          show={showModalIdle}
          onHide={undefined}
          dialogClassName='modal-dialog-centered'
          contentClassName='rounded-4 mh-400-px d-flex justify-content-between shadow border-0'
          size='lg'>
          <Container fluid className='px-0 g-0 '>
            <Row className='d-flex justify-content-center g-0  '>
              <Col lg={12} className='mt-5 pt-3'>
                <Col className='d-flex justify-content-center mb-4 text-secondary-1 g-0  '>
                  <Image
                    src={MODAL_IDLE_URL}
                    width={200}
                    alt='gif'
                    onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                      e.currentTarget.src = ErrorImage;
                    }}
                  />
                </Col>
              </Col>

              <Col lg={9} className='d-flex justify-content-center text-secondary-1 g-0 my-0 px-0'>
                <span className=' text-secondary-1 p-large-bold text-center'>{MODAL_IDLE_TITLE}</span>
              </Col>

              <Col lg={8} className='d-flex justify-content-center mt-2 mb-4 g-0 px-2 px-5'>
                <Button className={`my-2  svg-primary-4 btn-primary-icon-standard `} type='button' onClick={() => setShowModalIdle(false)}>
                  {MODAL_IDLE_UPDATE_BUTTON}
                </Button>
              </Col>
            </Row>
          </Container>
          <Col xs={12} className='align-self-end pt-2 '>
            <div className={'bg-primary-3 pt-2 rounded-4 rounded-top-0 '} />
          </Col>
        </Modal>
      )}

      {/* Fin Modal inactividad */}
      {showReservationModal && ( //Modal de proceso de Reserva
        <ReservationsModals />
      )}
      {showErrorReservationModal && ( // Modal de proceso de Reserva con Error
        <ReservationsModals error />
      )}
    </>
  );
};

export default Cart;
